import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { db } from "../../config/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import ModalDialogTitle from "../common/ModalDialogTitle";
import ModalActions from "../common/ModalActions";

const USER_TYPES = [
  "Staff",
  "Coordinator",
  "Instructor",
  "Admin",
  "Pathway Manager",
];
const PAGES = [
  "Course Catalog",
  "Course Shell",
  "Credit Capture",
  "Credit Capture Status",
  "Credit Types",
  "Ctag",
  "Data", // permission entry for the admin-only Data relationship visualizer
  "Delivery Details",
  "Divisions",
  "Elearn Shell Term",
  "Elearn Shell Type",
  "FPS",
  "FPS Courses",
  "HEI Programs",
  "Partners",
  "Pathway Programs",
  "Program Delivery",
  "PTP Forms",
  "PTP Status Note",
  "Residence Status Codes",
  "Shell Status",
  "Course Shell Status",
  "Sites",
  "SSN Status Code",
  "Students",
  "Showcase Categories", // permission entry for Showcase Categories admin area
  "Showcase Locations", // permission entry for Showcase Locations admin area
  "Showcase Participation Status", // permission entry for Showcase Participation Status admin area
  "Showcase Submissions", // permission entry for Showcase Submissions admin area
  "Showcase Email Template", // permission entry for Showcase Email Template admin area
  // "Showcase Site Categories", // removed: obsolete admin area
];

const TpUserTypes = ({ open, onClose }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (selectedType) {
      setLoading(true);
      getDoc(doc(db, "TpUserTypes", selectedType)).then((snap) => {
        if (snap.exists()) {
          setPermissions(snap.data().permissions || {});
        } else {
          // fallback: all false
          const perms = {};
          PAGES.forEach((p) => (perms[p] = { view: false, edit: false }));
          setPermissions(perms);
        }
        setLoading(false);
      });
    } else {
      setPermissions({});
    }
  }, [selectedType]);

  const handleSwitch = (page, field) => (e) => {
    setPermissions((prev) => ({
      ...prev,
      [page]: { ...prev[page], [field]: e.target.checked },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "TpUserTypes", selectedType), {
        name: selectedType,
        permissions,
      });
      setSnackbar({
        open: true,
        message: "Permissions saved!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving permissions",
        severity: "error",
      });
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <ModalDialogTitle onClose={onClose}>
        User Types Permissions
      </ModalDialogTitle>
      <DialogContent>
        <Box display="flex" justifyContent="center" mb={2}>
          <ToggleButtonGroup
            value={selectedType}
            exclusive
            onChange={(e, val) => setSelectedType(val)}
            aria-label="user type"
          >
            {USER_TYPES.map((type) => (
              <ToggleButton key={type} value={type} aria-label={type}>
                {type}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {selectedType && (
          <Box mt={3}>
            <Typography variant="h6">{selectedType} Permissions</Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {PAGES.map((page) => (
                  <Grid item xs={12} md={6} lg={4} key={page}>
                    <Box
                      border={1}
                      borderRadius={2}
                      p={2}
                      mb={2}
                      borderColor="#eee"
                    >
                      <Typography variant="subtitle1" gutterBottom>
                        {page}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!permissions[page]?.view}
                            onChange={handleSwitch(page, "view")}
                          />
                        }
                        label="View"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!permissions[page]?.edit}
                            onChange={handleSwitch(page, "edit")}
                          />
                        }
                        label="Edit"
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DialogContent>
      <ModalActions
        onSave={handleSave}
        saving={saving}
        saveDisabled={!selectedType || loading}
      />
    </Dialog>
  );
};

export default TpUserTypes;
